import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import { GeofenceData, EditableLayer } from './geo-manager.interfaces';

@Injectable({
  providedIn: 'root'
})
export class MapService {

  constructor() { }

  /**
   * Adds a geofence to the map as a layer
   */
  addGeofenceToMap(
    geofence: GeofenceData, 
    geofenceLayerGroup: L.LayerGroup, 
    drawnGeofences: L.Layer[],
    editableMap?: any,
    onLayerClick?: (geofence: GeofenceData) => void
  ): L.Layer | null {
    let layer: L.Layer;
    
    // Get color properties with fallbacks
    const color = geofence.color || geofence.style?.color || '#3B82F6';
    const fillColor = geofence.fillColor || geofence.style?.fillColor || '#3B82F650';
    const fillOpacity = geofence.style?.fillOpacity || 0.3;
    const weight = geofence.style?.weight || 2;
    
    const style = {
      color,
      fillColor,
      weight,
      fillOpacity
    };
    
    switch (geofence.type) {
      case 'polygon':
        // For editable polygons, we might need to create through editableMap
        if (editableMap && editableMap.createPolygon) {
          layer = editableMap.createPolygon(geofence.coordinates as [number, number][], style);
        } else {
          layer = L.polygon(geofence.coordinates as [number, number][], style);
        }
        break;
        
      case 'rectangle':
        const coords = geofence.coordinates as [number, number][];
        const bounds = L.latLngBounds(coords[0], coords[1]);
        
        if (editableMap && editableMap.createRectangle) {
          layer = editableMap.createRectangle(bounds, style);
        } else {
          layer = L.rectangle(bounds, style);
        }
        break;
        
      case 'circle':
        const center = geofence.center || geofence.coordinates?.[0] as [number, number];
        const circleStyle = {
          radius: geofence.radius || 1000,
          ...style
        };
        
        if (editableMap && editableMap.createCircle) {
          layer = editableMap.createCircle(center, circleStyle);
        } else {
          layer = L.circle(center, circleStyle);
        }
        break;
        
      default:
        return null;
    }

    // Add geofence data to layer
    const editableLayer: EditableLayer = layer as EditableLayer;
    editableLayer.geofenceData = geofence;
    
    // Add click handler if provided
    if (onLayerClick) {
      layer.on('click', () => {
        onLayerClick(geofence);
      });
    }

    // Add to layer group
    geofenceLayerGroup.addLayer(layer);
    drawnGeofences.push(layer);
    
    console.log('Added geofence layer:', geofence.type, 'with methods:', Object.getOwnPropertyNames(layer));
    return layer;
  }

  /**
   * Centers the map on a specific geofence
   */
  centerMapOnGeofence(map: L.Map, geofence: GeofenceData): void {
    // Calculate drawer width offset - typical drawer is about 320-400px wide
    const drawerWidth = 360; // Approximate drawer width in pixels
    const mapWidth = map.getSize().x;
    const drawerWidthRatio = drawerWidth / mapWidth;
    
    // Create more moderate padding that accounts for drawer
    const basePadding = 20;
    const drawerPadding = Math.min(drawerWidth * 0.3, 80); // More conservative drawer offset, max 80px
    const padding: [number, number] = [basePadding, drawerPadding + basePadding];
    
    if (geofence.type === 'circle' && geofence.center) {
      // For circles, create a small bounds around the center and fit with padding
      const radius = geofence.radius || 1000; // Default radius if not available
      const centerPoint = L.latLng(geofence.center);
      const radiusInDegrees = radius / 111320; // Approximate conversion from meters to degrees
      
      const bounds = L.latLngBounds([
        [centerPoint.lat - radiusInDegrees, centerPoint.lng - radiusInDegrees],
        [centerPoint.lat + radiusInDegrees, centerPoint.lng + radiusInDegrees]
      ]);
      
      map.fitBounds(bounds, { 
        padding: padding,
        maxZoom: 17 // Slightly higher max zoom for circles
      });
    } else if (geofence.type === 'polygon' && geofence.coordinates) {
      // For polygons, center on the bounds with drawer offset
      const bounds = L.latLngBounds(geofence.coordinates);
      map.fitBounds(bounds, { 
        padding: padding,
        maxZoom: 19 // Higher max zoom for polygons
      });
    } else if (geofence.type === 'rectangle' && geofence.coordinates) {
      // For rectangles, center on the bounds with drawer offset
      const bounds = L.latLngBounds(geofence.coordinates);
      map.fitBounds(bounds, { 
        padding: padding,
        maxZoom: 19 // Higher max zoom for rectangles
      });
    }
  }

  /**
   * Highlights a geofence by applying visual styling
   */
  highlightGeofence(geofence: GeofenceData, drawnGeofences: L.Layer[], clearHighlightsFn: () => void): void {
    // First, clear any existing highlights
    clearHighlightsFn();
    
    // Find the layer for this geofence
    const layer: EditableLayer = drawnGeofences.find(l => 
      (l as EditableLayer).geofenceData?.id === geofence.id
    ) as EditableLayer;
    
    if (layer && layer.setStyle) {
      // Always update the original style to reflect current geofence data
      // This ensures we restore to the correct color after highlighting
      layer._originalStyle = {
        color: geofence.color || geofence.style?.color || '#3B82F6',
        fillColor: geofence.fillColor || geofence.style?.fillColor || geofence.color || '#3B82F650',
        weight: geofence.style?.weight || 2,
        fillOpacity: geofence.style?.fillOpacity || 0.3,
        opacity: 1
      };
      
      // Bring the layer to front (highest z-index)
      if (typeof layer.bringToFront === 'function') {
        layer.bringToFront();
      }
      
      // Apply highlight style - just a thicker version of the current style
      const originalWeight = layer._originalStyle.weight || 2;
      layer.setStyle({
        ...layer._originalStyle,
        weight: originalWeight + 3, // Make it 3 pixels thicker
        opacity: 1,
        fillOpacity: layer._originalStyle.fillOpacity || 0.3
      });
      
      // Mark this layer as highlighted
      layer._isHighlighted = true;
      
      // Auto-clear highlight after 3 seconds
      setTimeout(() => {
        this.clearHighlight(layer);
      }, 3000);
    }
  }

  /**
   * Clears all highlights from layers
   */
  clearAllHighlights(drawnGeofences: L.Layer[]): void {
    drawnGeofences.forEach(layer => {
      this.clearHighlight(layer);
    });
  }

  /**
   * Clears highlight from a specific layer
   */
  clearHighlight(layer: L.Layer): void {
    const editableLayer = layer as EditableLayer;
    if (editableLayer._isHighlighted && editableLayer._originalStyle && editableLayer.setStyle) {
      // Restore original style
      editableLayer.setStyle(editableLayer._originalStyle);
      editableLayer._isHighlighted = false;
    }
  }

  /**
   * Makes a geofence visually editable
   */
  makeGeofenceVisuallyEditable(
    geofenceId: string, 
    drawnGeofences: L.Layer[], 
    editableMap: any,
    internalGeofences: GeofenceData[],
    updateGeofenceFromLayerFn: (geofence: GeofenceData, layer: L.Layer) => void
  ): void {
    const layer: EditableLayer = drawnGeofences.find(l => 
      (l as EditableLayer).geofenceData?.id === geofenceId
    ) as EditableLayer;
    
    if (layer) {
      console.log('Found layer for editing:', geofenceId);
      console.log('Layer type:', layer.constructor.name);
      
      try {
        // Enable vertex editing using Leaflet.Editable
        if (typeof layer.enableEdit === 'function') {
          console.log('Using layer.enableEdit() method');
          layer.enableEdit();
          console.log('Edit mode enabled successfully');
        } else if (typeof layer.toggleEdit === 'function') {
          console.log('Using layer.toggleEdit() method');
          layer.toggleEdit();
          console.log('Edit mode toggled successfully');
        } else if (editableMap && typeof editableMap.enableEdit === 'function') {
          console.log('Using editableMap.enableEdit() method');
          editableMap.enableEdit(layer);
        } else {
          console.log('editableMap methods not available, using fallback styling');
          this.fallbackVisualEditing(layer);
        }

        // Enable dragging for the entire geofence using leaflet.path.drag
        if (typeof layer.dragging?.enable === 'function') {
          console.log('Enabling path dragging for geofence');
          layer.dragging.enable();
          
          // Listen for drag events to update coordinates
          layer.on('dragend', () => {
            console.log('Geofence drag ended, updating coordinates');
            const geofence = internalGeofences.find(g => g.id === geofenceId);
            if (geofence) {
              updateGeofenceFromLayerFn(geofence, layer);
            }
          });
        } else {
          console.log('Dragging not available for this layer type');
        }

        // Also listen for vertex drag events when using Leaflet.Editable
        layer.on('editable:vertex:dragend', () => {
          console.log('Vertex drag ended, updating coordinates');
          const geofence = internalGeofences.find(g => g.id === geofenceId);
          if (geofence) {
            updateGeofenceFromLayerFn(geofence, layer);
          }
        });

      } catch (error) {
        console.error('Error enabling geofence editing:', error);
        // Fallback to manual styling if Leaflet.Editable fails
        this.fallbackVisualEditing(layer);
      }
    } else {
      console.error('Layer not found for geofence ID:', geofenceId);
    }
  }

  /**
   * Applies fallback visual editing style
   */
  private fallbackVisualEditing(layer: L.Layer): void {
    const editableLayer: EditableLayer = layer as EditableLayer;
    if (editableLayer.setStyle) {
      const currentStyle = editableLayer.options;
      editableLayer.setStyle({
        ...currentStyle,
        weight: 3,
        opacity: 0.8,
        dashArray: '5, 5', // Dashed border to indicate editing
        fillOpacity: 0.4
      });
    }
  }

  /**
   * Removes editable visual style from a layer
   */
  removeEditableVisualStyle(
    geofenceId: string, 
    drawnGeofences: L.Layer[], 
    internalGeofences: GeofenceData[]
  ): void {
    const layer: EditableLayer = drawnGeofences.find(l => 
      (l as EditableLayer).geofenceData?.id === geofenceId
    ) as EditableLayer;
    
    if (layer) {
      console.log('Disabling edit mode for layer:', geofenceId);
      
      // Disable Leaflet.Editable editing first
      try {
        if (layer.disableEdit) {
          console.log('Using layer.disableEdit() method');
          layer.disableEdit();
        } else if (layer.toggleEdit) {
          console.log('Using layer.toggleEdit() method to disable');
          layer.toggleEdit(); // This should disable if already enabled
        }

        // Disable dragging
        if (typeof layer.dragging?.disable === 'function') {
          console.log('Disabling path dragging for geofence');
          layer.dragging.disable();
        }

        // Remove event listeners
        layer.off('dragend');
        layer.off('editable:vertex:dragend');

      } catch (error) {
        console.error('Error disabling geofence editing:', error);
      }
      
      // Also restore original visual style
      if (layer.setStyle) {
        const geofence = internalGeofences.find(g => g.id === geofenceId);
        if (geofence) {
          // Restore original style
          layer.setStyle({
            color: geofence.color || geofence.style?.color || '#3B82F6',
            fillColor: geofence.fillColor || geofence.style?.fillColor || '#3B82F650',
            weight: geofence.style?.weight || 2,
            fillOpacity: geofence.style?.fillOpacity || 0.3,
            opacity: 1,
            dashArray: null
          });
        }
      }
    }
  }

  /**
   * Updates geofence data from the layer's current coordinates
   */
  updateGeofenceFromLayer(geofence: GeofenceData, layer: L.Layer): void {
    if (layer instanceof L.Polygon) {
      const latlngs = layer.getLatLngs()[0] as L.LatLng[];
      geofence.coordinates = latlngs.map(latlng => [latlng.lat, latlng.lng]);
    } else if (layer instanceof L.Rectangle) {
      const bounds = layer.getBounds();
      geofence.coordinates = [
        [bounds.getNorth(), bounds.getWest()],
        [bounds.getNorth(), bounds.getEast()],
        [bounds.getSouth(), bounds.getEast()],
        [bounds.getSouth(), bounds.getWest()]
      ];
    } else if (layer instanceof L.Circle) {
      const center = layer.getLatLng();
      geofence.center = [center.lat, center.lng];
      geofence.radius = layer.getRadius();
      geofence.coordinates = []; // Circle doesn't use coordinates array
    }
  }

  /**
   * Gets updated coordinates from an edited geofence layer
   */
  getUpdatedGeofenceCoordinates(geofenceId: string, drawnGeofences: L.Layer[]): Partial<GeofenceData> | null {
    const layer: EditableLayer = drawnGeofences.find(l => 
      (l as EditableLayer).geofenceData?.id === geofenceId
    ) as EditableLayer;
    
    if (!layer) {
      console.log('Layer not found for geofence ID:', geofenceId);
      return null;
    }
    
    try {
      if (layer instanceof L.Polygon) {
        const latlngs = layer.getLatLngs()[0] as L.LatLng[];
        const coordinates: [number, number][] = latlngs.map(latlng => [latlng.lat, latlng.lng]);
        console.log('Updated polygon coordinates:', coordinates);
        return { coordinates };
      } else if (layer instanceof L.Rectangle) {
        const bounds = layer.getBounds();
        const coordinates: [number, number][] = [
          [bounds.getSouth(), bounds.getWest()],
          [bounds.getNorth(), bounds.getEast()]
        ];
        console.log('Updated rectangle coordinates:', coordinates);
        return { coordinates };
      } else if (layer instanceof L.Circle) {
        const center = layer.getLatLng();
        const radius = layer.getRadius();
        console.log('Updated circle center and radius:', [center.lat, center.lng], radius);
        return { 
          center: [center.lat, center.lng] as [number, number],
          radius: radius
        };
      } else {
        console.log('Unknown layer type, cannot extract coordinates');
        return null;
      }
    } catch (error) {
      console.error('Error extracting coordinates from layer:', error);
      return null;
    }
  }

  /**
   * Updates the color of a geofence layer
   */
  updateGeofenceColor(geofenceId: string, newColor: string, drawnGeofences: L.Layer[]): void {
    const layer: EditableLayer = drawnGeofences.find(l => 
      (l as EditableLayer).geofenceData?.id === geofenceId
    ) as EditableLayer;

    if (layer && layer.setStyle) {
      layer.setStyle({
        color: newColor,
        fillColor: newColor
      });
    }
  }

  /**
   * Restores the original geometry of a layer from stored geofence data
   */
  restoreLayerGeometry(layer: L.Layer, originalGeofence: GeofenceData): void {
    try {
      console.log('Restoring geometry for layer type:', layer.constructor.name, 'to original state');
      
      if (layer instanceof L.Polygon && originalGeofence.coordinates) {
        // Convert coordinates back to LatLng array and update the polygon
        const latLngs = originalGeofence.coordinates.map(coord => L.latLng(coord[0], coord[1]));
        console.log('Restoring polygon with coordinates:', latLngs);
        layer.setLatLngs([latLngs]);
        
      } else if (layer instanceof L.Rectangle && originalGeofence.coordinates) {
        // For rectangles, restore using bounds from coordinates
        const coords = originalGeofence.coordinates;
        if (coords.length >= 2) {
          const bounds = L.latLngBounds(coords[0], coords[2] || coords[1]);
          console.log('Restoring rectangle with bounds:', bounds);
          layer.setBounds(bounds);
        }
        
      } else if (layer instanceof L.Circle && originalGeofence.center && originalGeofence.radius) {
        // For circles, restore center and radius
        const center = L.latLng(originalGeofence.center[0], originalGeofence.center[1]);
        console.log('Restoring circle with center:', center, 'radius:', originalGeofence.radius);
        layer.setLatLng(center);
        layer.setRadius(originalGeofence.radius);
        
      } else {
        console.warn('Unable to restore geometry for layer type:', layer.constructor.name);
      }
      
      console.log('Geometry restoration completed successfully');
      
    } catch (error) {
      console.error('Error restoring layer geometry:', error);
    }
  }

  /**
   * Creates a geofence data object from a layer
   */
  createGeofenceFromLayer(layer: L.Layer, geofencesCount: number): GeofenceData | null {
    const id = 'geofence_' + Date.now();
    const name = `Geocerca ${geofencesCount + 1}`;
    
    if (layer instanceof L.Polygon) {
      const latlngs = layer.getLatLngs()[0] as L.LatLng[];
      const coordinates: [number, number][] = latlngs.map(latlng => [latlng.lat, latlng.lng]);
      return {
        id,
        name,
        type: 'polygon',
        coordinates,
        color: '#3B82F6',
        fillColor: '#3B82F650'
      };
    } else if (layer instanceof L.Rectangle) {
      const bounds = layer.getBounds();
      return {
        id,
        name,
        type: 'rectangle',
        coordinates: [
          [bounds.getSouth(), bounds.getWest()],
          [bounds.getNorth(), bounds.getEast()]
        ],
        color: '#3B82F6',
        fillColor: '#3B82F650'
      };
    } else if (layer instanceof L.Circle) {
      const center = layer.getLatLng();
      return {
        id,
        name,
        type: 'circle',
        center: [center.lat, center.lng],
        radius: layer.getRadius(),
        color: '#3B82F6',
        fillColor: '#3B82F650'
      };
    }

    return null;
  }

  /**
   * Starts drawing based on the geofence type
   */
  startActualDrawing(
    creatingGeofenceType: 'polygon' | 'rectangle' | 'circle',
    editableMap: any,
    map: L.Map,
    onGeofenceDrawingCompleteFn: (event: any) => void
  ): void {
    if (!editableMap || !creatingGeofenceType) return;

    switch (creatingGeofenceType) {
      case 'polygon':
        editableMap.startPolygon();
        break;
      case 'rectangle':
        editableMap.startRectangle();
        break;
      case 'circle':
        editableMap.startCircle();
        break;
    }

    // Listen for drawing completion
    map.on('editable:drawing:commit', onGeofenceDrawingCompleteFn);
  }

  /**
   * Cancels any active drawing
   */
  cancelDrawing(editableMap: any, map: L.Map, onGeofenceDrawingCompleteFn: (event: any) => void): void {
    if (editableMap) {
      editableMap.stopDrawing();
      map.off('editable:drawing:commit', onGeofenceDrawingCompleteFn);
    }
  }

  /**
   * Updates the style of a drawn geofence (typically used during creation)
   */
  updateDrawnGeofenceColor(layer: L.Layer | null, color: string): void {
    if (layer && layer instanceof L.Path) {
      layer.setStyle({ color: color, fillColor: color });
    }
  }

  /**
   * Removes a layer from the map and layer group
   */
  removeLayerFromMap(layer: L.Layer, geofenceLayerGroup: L.LayerGroup, drawnGeofences: L.Layer[]): void {
    geofenceLayerGroup.removeLayer(layer);
    const layerIndex = drawnGeofences.indexOf(layer);
    if (layerIndex !== -1) {
      drawnGeofences.splice(layerIndex, 1);
    }
  }

  /**
   * Clears all geofences from the map
   */
  clearAllGeofences(geofenceLayerGroup: L.LayerGroup, drawnGeofences: L.Layer[]): void {
    geofenceLayerGroup.clearLayers();
    drawnGeofences.length = 0; // Clear the array
  }
}
